import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

export interface HavFiltersState {
  state: string;
  type: string;
  name: string;
  sponsor: string;
  principal: string;
  paymentStatus: string;
}

interface HavFiltersProps {
  filters: HavFiltersState;
  onChange: (filters: HavFiltersState) => void;
}

const initialFilters: HavFiltersState = {
  state: "all",
  type: "all",
  name: "",
  sponsor: "",
  principal: "",
  paymentStatus: "",
};

export const HavFilters = ({ filters, onChange }: HavFiltersProps) => {
  const update = (key: keyof HavFiltersState, value: string) => {
    onChange({ ...filters, [key]: value });
  };

  const reset = () => onChange(initialFilters);

  const hasFilters =
    filters.state !== "all" ||
    filters.type !== "all" ||
    filters.name.trim() !== "" ||
    filters.sponsor.trim() !== "" ||
    filters.principal.trim() !== "" ||
    filters.paymentStatus.trim() !== "";

  return (
    <div className="rounded-md border bg-card p-3 mb-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end">
        <div className="space-y-1">
          <Label htmlFor="hav-filter-state" className="text-xs">
            État
          </Label>
          <Select
            value={filters.state}
            onValueChange={(v) => update("state", v)}
          >
            <SelectTrigger id="hav-filter-state" className="h-8 text-xs">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent className="z-[100] bg-white border shadow-lg">
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="approved">Validé</SelectItem>
              <SelectItem value="refused">Refusé</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="hav-filter-type" className="text-xs">
            Type
          </Label>
          <Select
            value={filters.type}
            onValueChange={(v) => update("type", v)}
          >
            <SelectTrigger id="hav-filter-type" className="h-8 text-xs">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent className="z-[100] bg-white border shadow-lg">
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="principal">Principal</SelectItem>
              <SelectItem value="accompagnant">Accompagnant</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label htmlFor="hav-filter-name" className="text-xs">
            Nom / Prénom
          </Label>
          <Input
            id="hav-filter-name"
            value={filters.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Filtrer..."
            className="h-8 text-xs"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="hav-filter-sponsor" className="text-xs">
            Parrain
          </Label>
          <Input
            id="hav-filter-sponsor"
            value={filters.sponsor}
            onChange={(e) => update("sponsor", e.target.value)}
            placeholder="Filtrer..."
            className="h-8 text-xs"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="hav-filter-principal" className="text-xs">
            Participant principal
          </Label>
          <Input
            id="hav-filter-principal"
            value={filters.principal}
            onChange={(e) => update("principal", e.target.value)}
            placeholder="Filtrer..."
            className="h-8 text-xs"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="hav-filter-payment" className="text-xs">
            Statut paiement
          </Label>
          <Input
            id="hav-filter-payment"
            value={filters.paymentStatus}
            onChange={(e) => update("paymentStatus", e.target.value)}
            placeholder="Filtrer..."
            className="h-8 text-xs"
          />
        </div>
      </div>

      {hasFilters && (
        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={reset}
            className="h-7 text-xs"
          >
            <X className="h-3 w-3 mr-1" />
            Réinitialiser les filtres
          </Button>
        </div>
      )}
    </div>
  );
};
